// src/lib/api/misc.ts — misc public endpoints (PRI-291 Phase 7 → PRI-439 R13)
// prompts, waitlist, invites, model info, demo signals, user profile
//
// R13: all calls ride the SDK transport via request() (see client.ts).

import { API_BASE_URL } from './client'
// PRI-496 P0: internal (non-public-contract) endpoints route through the
// internal-api-client chokepoint. internalRequest === legacy request() — pure
// delegation, zero behavior change. (probeAccessRestriction targets /v1/usage,
// which IS in the public contract, so it stays on a direct fetch.)
import { internalRequest as request } from '../internal-api-client'

// R12 (PRI-430, 2026-07-08): the mock-era "legacy V1" /v1/videos/* client
// (processVideo/getJobStatus/getJobResult/queryVideo/deleteJob + VideoJob/
// JobStatus/JobResult types) is DELETED. Those endpoints never existed on the
// real API — they targeted a mock server from the pre-launch demo era and had
// zero production call sites. The /v1/videos namespace is reserved for the
// public API v1 (design §13).

// PRI-496 Lane B: ModelInfo + getModelInfo (internal GET /v1/model-info) are
// DELETED — the product UI reads the public model catalog instead
// (getDefaultPublicModel in ./public-inference → GET /v1/models). Checkpoint
// metadata (vl_step/jepa_step) is intentionally absent from the public
// contract — it is internal model provenance, admin-only via the console's
// /v1/model-info-proxy (2026-06-26 leak fix).

// ── Waitlist (public) ─────────────────────────────────────────────────────────

export function submitWaitlist(data: {
  email: string
  full_name: string
  company?: string
  use_case?: string
  vertical?: string
}): Promise<{ id: string; email: string; status: string }> {
  return request('/v1/waitlist', { method: 'POST', body: JSON.stringify(data) })
}

export function validateInviteToken(token: string): Promise<{ valid: boolean; email: string; full_name: string }> {
  return request(`/v1/invite/validate?token=${encodeURIComponent(token)}`)
}

export function acceptInvite(token: string): Promise<{ accepted: boolean }> {
  return request('/v1/invite/accept', { method: 'POST', body: JSON.stringify({ token }) })
}

// ── Prompts & model info ──────────────────────────────────────────────────────

export function getPrompts(): Promise<{ prompts: string[] }> {
  return request('/v1/prompts');
}

// ── Feedback ───────────────────────────────────────────────────────────────────

/** Fire-and-forget narrative feedback vote (POST /v1/feedback). Never throws. */
export function sendNarrativeFeedback(data: {
  verdict: 'up' | 'down'
  narrative_text: string
  entry_index: number
  search_terms: string[]
}): void {
  request('/v1/feedback', { method: 'POST', body: JSON.stringify(data) }).catch(() => {})
}

// ── Usage / restriction probe ─────────────────────────────────────────

/**
 * Probe /v1/usage with an explicit token to detect account restriction.
 * Returns 'restricted' | 'ok' | 'unknown'. Used by PrimateAuthContext, which
 * deliberately bypasses the module-level token getter during auth bootstrap.
 */
export async function probeAccessRestriction(token: string): Promise<'restricted' | 'ok' | 'unknown'> {
  try {
    const res = await fetch(`${API_BASE_URL}/v1/usage`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (res.status === 403) {
      const body = await res.json().catch(() => ({} as Record<string, unknown>))
      if ((body as { error?: { code?: string } })?.error?.code === 'access_restricted') return 'restricted'
      return 'unknown'
    }
    return res.ok ? 'ok' : 'unknown'
  } catch {
    return 'unknown'
  }
}

// ── Active job (session restore) ───────────────────────────────────────

export interface ActiveJob {
  job_id?: string
  status?: string
  prompt?: string
  queue_position?: number
}

// PRI-496 Lane B: getActiveJob (internal GET /v1/video/active-job) is
// deleted — session restore uses getActiveAnalysis in ./public-inference
// (public GET /v1/analyses?status=…). ActiveJob type retained for its shape.

// ── Demo videos (public, no auth) ───────────────────────────────────────

// getDemoVideos lives in ./user-videos (returns { videos: DemoVideo[] }).

/** Direct URL for a demo video file — public GET /v1/demo-videos/{filename}, no auth. */
export function getDemoVideoUrl(filename: string): string {
  return `${API_BASE_URL}/v1/demo-videos/${encodeURIComponent(filename)}`
}

/** URL of the public OpenAPI spec (served by the API). */
export function getOpenApiSpecUrl(): string {
  return `${API_BASE_URL}/v1/openapi.json`
}

// ── Intercom token ────────────────────────────────────────────────────────────

export async function getIntercomToken(): Promise<{ token: string }> {
  try {
    return await request<{ token: string }>('/v1/intercom-token')
  } catch {
    throw new Error('Failed to get Intercom token')
  }
}

// ── User profile ─────────────────────────────────────────────────────────────
// (postDemoSignal deleted — PRI-433 P4 removed /v1/demo/signal server-side.)

export function updateUserProfile(data: {
  vertical?: string
  replacing?: string
}): Promise<{ id: string; vertical: string | null; replacing: string | null }> {
  return request('/v1/user/profile', { method: 'PATCH', body: JSON.stringify(data) })
}

export interface UserProfile {
  id: string
  vertical: string | null
  replacing: string | null
  /** null => user has NOT accepted the Terms & Conditions yet. */
  terms_accepted_at: string | null
}

/**
 * Fetch the authenticated user's profile. Used by the first-run consent gate to
 * read the server source-of-truth for terms acceptance (so a user who accepted
 * on another device is not re-prompted).
 */
export function getUserProfile(): Promise<UserProfile> {
  return request<UserProfile>('/v1/user/profile')
}

/**
 * Record first-run Terms & Conditions acceptance server-side. Idempotent: the
 * API only sets terms_accepted_at if currently null (first acceptance wins).
 * Returns the (possibly pre-existing) acceptance timestamp.
 */
export function acceptTerms(): Promise<UserProfile> {
  return request<UserProfile>('/v1/user/profile', {
    method: 'PATCH',
    body: JSON.stringify({ terms_accepted: true }),
  })
}

// ── Design Partner Agreements (click-to-sign / click-to-pay, public token-gated) ──

export interface DesignPartnerAgreementPublic {
  companyLegalName: string
  useCase: string
  valueOfSolving: string | null
  targetMetrics: string
  scopeOfWork: string | null
  estimatedCostToDeliver: string | null
  priceUsd: number | null
  publicReferenceOptOut: boolean
  status: 'draft' | 'sent' | 'signed' | 'paid' | 'expired'
  expiresAt: string
  signedAt: string | null
  paidAt: string | null
}

export function getDesignPartnerAgreement(token: string): Promise<DesignPartnerAgreementPublic> {
  return request<DesignPartnerAgreementPublic>(`/v1/design-partner-agreements/by-token/${encodeURIComponent(token)}`)
}

export interface SignDesignPartnerAgreementResult {
  status: 'signed'
  requiresPayment: boolean
  nextStep: 'create_checkout_session' | 'none'
}

export function signDesignPartnerAgreement(
  token: string,
  data: { signerName: string; signerTitle: string; agreedToTerms: true },
): Promise<SignDesignPartnerAgreementResult> {
  return request<SignDesignPartnerAgreementResult>(
    `/v1/design-partner-agreements/by-token/${encodeURIComponent(token)}/sign`,
    { method: 'POST', body: JSON.stringify(data) },
  )
}

export function createDesignPartnerCheckoutSession(token: string): Promise<{ checkoutUrl: string }> {
  return request<{ checkoutUrl: string }>(
    `/v1/design-partner-agreements/by-token/${encodeURIComponent(token)}/create-checkout-session`,
    { method: 'POST', body: JSON.stringify({}) },
  )
}
